import React, { useEffect, useMemo, useState } from 'react';
import { Button, Form, Input } from 'antd';
import { Link, useNavigate } from 'react-router-dom';

import { useLogin } from 'services/auth/mutations';

interface FormLoginProps {
  redirectUrl?: string | null;
}
const FormLogin = ({ redirectUrl }: FormLoginProps) => {
  const [form] = Form.useForm();
  const { data, fetching, error, execute: login } = useLogin();
  const navigate = useNavigate();

  const [formData, setFormData] = useState<any>(null);
  const [focusedFields, setFocusedFields] = useState<Record<string, boolean>>({
    email: false,
    password: false
  });

  const emailValue = Form.useWatch('email', form);
  const passwordValue = Form.useWatch('password', form);

  const showEmailLabel = useMemo(
    () => focusedFields.email || !!emailValue,
    [focusedFields.email, emailValue]
  );
  const showPasswordLabel = useMemo(
    () => focusedFields.password || !!passwordValue,
    [focusedFields.password, passwordValue]
  );

  useEffect(() => {
    if (data) {
      localStorage.setItem('token', data.login.authToken);
      window.dispatchEvent(new Event('storage'));
      if (redirectUrl) {
        window.open(redirectUrl, '_blank');
        navigate('/dashboard', { replace: true });
      } else {
        navigate('/dashboard', { replace: true });
      }
    }
  }, [data, redirectUrl, navigate]);

  const onFinish = (values: any) => {
    setFormData(values);
    login({ loginUserInput: values });
  };

  const onFinishFailed = (errorInfo: any) => {
    console.log('Failed:', errorInfo);
  };

  return (
    <Form
      form={form}
      name="basic"
      onFinish={onFinish}
      onFinishFailed={onFinishFailed}
      className="form"
      autoComplete="off"
    >
      <Form.Item
        name="email"
        className="form-item"
        rules={[
          {
            required: true,
            message: 'Please input your username!'
          }
        ]}
      >
        <div className={`form-item__wrapper ${showEmailLabel ? 'form-item__wrapper--with-label' : ''}`}>
          {showEmailLabel && (
            <label className="floating-label" htmlFor="login-email">
              Email address
            </label>
          )}
          <Input
            id="login-email"
            placeholder="Email address"
            className="form-item__input"
            onFocus={() =>
              setFocusedFields((prev) => ({
                ...prev,
                email: true
              }))
            }
            onBlur={(event) =>
              setFocusedFields((prev) => ({
                ...prev,
                email: false
              }))
            }
          />
        </div>
      </Form.Item>

      <Form.Item
        name="password"
        className="form-item"
        rules={[
          {
            required: true,
            message: 'Please input your password!'
          }
        ]}
      >
        <div className={`form-item__wrapper ${showPasswordLabel ? 'form-item__wrapper--with-label' : ''}`}>
          {showPasswordLabel && (
            <label className="floating-label" htmlFor="login-password">
              Password
            </label>
          )}
          <Input.Password
            id="login-password"
            placeholder="Password"
            className="form-item__input"
            onFocus={() =>
              setFocusedFields((prev) => ({
                ...prev,
                password: true
              }))
            }
            onBlur={() =>
              setFocusedFields((prev) => ({
                ...prev,
                password: false
              }))
            }
          />
        </div>
      </Form.Item>
      <div className='reset-password'>
        <Link className="body-regular link" to="/reset-password">
          Forgot password? <span className="body-regular-bold">Reset it</span>
        </Link>
      </div>

      <div className="error-message">
        {error &&
          'Invalid username or password. Please check your credentials and try again.'}
      </div>

      <Form.Item>
        <Button
          className="button button-primary button-txt-big"
          loading={fetching}
          htmlType="submit"
        >
          Sign in
        </Button>
      </Form.Item>
    </Form>
  );
};
export default FormLogin;
